import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import { PatternFormat } from 'react-number-format';
import CreatableSelect from 'react-select/creatable';

const medicationOptions = [
  { value: 'Sertraline', label: 'Sertraline' },
  { value: 'Bupropion', label: 'Bupropion' },
  { value: 'Escitalopram', label: 'Escitalopram' },
  { value: 'Venlafaxine', label: 'Venlafaxine' },
  { value: 'Trazodone', label: 'Trazodone' },
  { value: 'Fluoxetine', label: 'Fluoxetine' },
  { value: 'Paroxetine', label: 'Paroxetine' },
  { value: 'Citalopram', label: 'Citalopram' },
  { value: 'Duloxetine', label: 'Duloxetine' },
  { value: 'Mirtazapine', label: 'Mirtazapine' },
  { value: 'Amitriptyline', label: 'Amitriptyline' },
  { value: 'Nortriptyline', label: 'Nortriptyline' },
  { value: 'Imipramine', label: 'Imipramine' },
  { value: 'Desipramine', label: 'Desipramine' },
  { value: 'Lithium', label: 'Lithium' },
  { value: 'Lamotrigine', label: 'Lamotrigine' },
  { value: 'Valproate', label: 'Valproate' },
  { value: 'Divalproex', label: 'Divalproex' },
  { value: 'Carbamazepine', label: 'Carbamazepine' },
  { value: 'Oxcarbazepine', label: 'Oxcarbazepine' },
  { value: 'Aripiprazole', label: 'Aripiprazole' },
  { value: 'Risperidone', label: 'Risperidone' },
  { value: 'Olanzapine', label: 'Olanzapine' },
  { value: 'Quetiapine', label: 'Quetiapine' },
  { value: 'Ziprasidone', label: 'Ziprasidone' },
  { value: 'Lurasidone', label: 'Lurasidone' },
  { value: 'Clozapine', label: 'Clozapine' },
  { value: 'Haloperidol', label: 'Haloperidol' },
  { value: 'Chlorpromazine', label: 'Chlorpromazine' },
  { value: 'Perphenazine', label: 'Perphenazine' },
  { value: 'Thioridazine', label: 'Thioridazine' },
  { value: 'Buspirone', label: 'Buspirone' },
  { value: 'Hydroxyzine', label: 'Hydroxyzine' },
  { value: 'Propranolol', label: 'Propranolol' },
  { value: 'Clonazepam', label: 'Clonazepam' },
  { value: 'Lorazepam', label: 'Lorazepam' },
  { value: 'Diazepam', label: 'Diazepam' },
  { value: 'Alprazolam', label: 'Alprazolam' },
  { value: 'Temazepam', label: 'Temazepam' },
  { value: 'Zolpidem', label: 'Zolpidem' },
  { value: 'Eszopiclone', label: 'Eszopiclone' },
  { value: 'Trazodone', label: 'Trazodone' },
  { value: 'Modafinil', label: 'Modafinil' },
  { value: 'Atomoxetine', label: 'Atomoxetine' },
  { value: 'Methylphenidate', label: 'Methylphenidate' },
  { value: 'Dexmethylphenidate', label: 'Dexmethylphenidate' },
  { value: 'Amphetamine/Dextroamphetamine', label: 'Amphetamine/Dextroamphetamine' },
  { value: 'Lisdexamfetamine', label: 'Lisdexamfetamine' },
  { value: 'Guanfacine', label: 'Guanfacine' },
  { value: 'Clonidine', label: 'Clonidine' },
];

export default function MedicationRefillPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    nextAppointmentStatus: '',
    nextAppointmentDate: '',
    newAllergy: '',
    newAllergyName: '',
    requestType: '',
    reasonDetails: '',
    medicationSelection: '',
    pharmacy: '',
    pharmacyChangeType: '',
    pharmacyType: '',
    localPharmacy: '',
    otherLocalPharmacy: '',
    mailOrderPharmacy: '',
    otherMailOrderPharmacy: '',
    pharmacyCrossRoads: '',
    preferredContact: '',
    email: '',
    phone: '',
    voicemailPermission: '',
    smsPermission: '',
  });

  const [medications, setMedications] = useState([{ id: 0, name: '', strength: '' }]);
  const [inputValues, setInputValues] = useState({});
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Check if user selected "Need to Schedule" - show modal immediately
    if (name === 'nextAppointmentStatus' && value === 'Need to Schedule') {
      setShowScheduleModal(true);
    }
  };

  const handleMedicationChange = (index, field, value) => {
    const updated = [...medications];
    updated[index][field] = value;
    setMedications(updated);
  };

  const addMedication = () => {
    if (medications.length < 10) {
      setMedications((prev) => [...prev, { id: Date.now(), name: '', strength: '' }]);
    }
  };

  const removeMedication = (id) => {
    setMedications((prev) => prev.filter((m) => m.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Simple client-side check for required fields only
    if (formData.nextAppointmentStatus === 'Need to Schedule') {
      setShowScheduleModal(true);
      return;
    }
    
    // Let HTML5 validation handle most cases, backend handles security
    setIsSubmitting(true);
    
    const payload = { 
      ...formData, 
      medications: formData.medicationSelection === 'All Meds' ? [] : medications.filter(m => m.name.trim())
    };
    
    try {
      const res = await fetch('/api/medication-refill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      
      if (data.success) {
        alert('Your prescription request has been submitted successfully!');
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          dob: '',
          nextAppointmentStatus: '',
          nextAppointmentDate: '',
          newAllergy: '',
          newAllergyName: '',
          requestType: '',
          reasonDetails: '',
          medicationSelection: '',
          pharmacy: '',
          pharmacyChangeType: '',
          pharmacyType: '',
          localPharmacy: '',
          otherLocalPharmacy: '',
          mailOrderPharmacy: '',
          otherMailOrderPharmacy: '',
          pharmacyCrossRoads: '',
          preferredContact: '',
          email: '',
          phone: '',
          voicemailPermission: '',
          smsPermission: '',
        });
        setMedications([{ id: 0, name: '', strength: '' }]);
      } else {
        // Backend validation provides detailed error messages
        if (data.errors && Array.isArray(data.errors)) {
          alert('Please fix the following:\n\n' + data.errors.join('\n'));
        } else {
          alert(data.message || 'Please check your form and try again.');
        }
      }
    } catch (err) {
      console.error('Error submitting prescription form:', err);
      alert('Server error. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Prescription Request | Redemption Psychiatry</title>
      </Helmet>

      <Header />
      
      {/* Schedule Appointment Modal */}
      {showScheduleModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h1 className="modal-title w-100 text-center mb-0">Appointment Required</h1>
              </div>
              <div className="modal-body text-center px-4">
                <p className="mb-3">You must schedule an appointment before requesting prescription refills.</p>
                <p className="mb-4">Please call one of our offices:</p>
                
                <div className="row g-3 mb-4">
                  <div className="col-12 col-sm-6 col-lg-3">
                    <a 
                      className="btn btn-primary w-100 py-3" 
                      href="tel:+14805420202"
                      style={{ 
                        backgroundColor: '#4a90e2', 
                        borderColor: '#4a90e2',
                        fontSize: '14px',
                        lineHeight: '1.2'
                      }}
                    >
                      <strong>Gilbert</strong><br />
                      <span>(480) 542-0202</span>
                    </a>
                  </div>
                  <div className="col-12 col-sm-6 col-lg-3">
                    <a 
                      className="btn btn-primary w-100 py-3" 
                      href="tel:+14805420222"
                      style={{ 
                        backgroundColor: '#4a90e2', 
                        borderColor: '#4a90e2',
                        fontSize: '14px',
                        lineHeight: '1.2'
                      }}
                    >
                      <strong>Chandler</strong><br />
                      <span>(480) 542-0222</span>
                    </a>
                  </div>
                  <div className="col-12 col-sm-6 col-lg-3">
                    <a 
                      className="btn btn-primary w-100 py-3" 
                      href="tel:+14805421212"
                      style={{ 
                        backgroundColor: '#4a90e2', 
                        borderColor: '#4a90e2',
                        fontSize: '14px',
                        lineHeight: '1.2'
                      }}
                    >
                      <strong>Mesa</strong><br />
                      <span>(480) 542-1212</span>
                    </a>
                  </div>
                  <div className="col-12 col-sm-6 col-lg-3">
                    <a 
                      className="btn btn-primary w-100 py-3" 
                      href="tel:+16025603505"
                      style={{ 
                        backgroundColor: '#4a90e2', 
                        borderColor: '#4a90e2',
                        fontSize: '14px',
                        lineHeight: '1.2'
                      }}
                    >
                      <strong>Phoenix</strong><br />
                      <span>(602) 560-3505</span>
                    </a>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0 justify-content-center">
                <button 
                  type="button" 
                  className="btn btn-primary w-50 px-4" 
                  onClick={() => {
                    setShowScheduleModal(false);
                    setFormData(prev => ({ ...prev, nextAppointmentStatus: '' }));
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container-fluid px-3 px-md-5 py-5" id="content">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10 col-xl-8">
            <h2 className="mb-4 text-center">Prescription Request Form</h2>
            <p className="mb-4">
              If you have a prescription related request, you may use this form to give our staff the information they need to process your request. If you don't have a follow-up appointment scheduled, you need to call the office and schedule an appointment before requesting any medication refills. Requests are processed in the order received, please do not submit duplicate requests.
            </p>

            <div className="alert alert-primary">
              <strong>Use of this form does not guarantee you will automatically receive every medication you request, requests are only processed during normal business hours.</strong>
              <br /><br />
              <strong>DO NOT</strong> use this form if:
              <p style={{ marginLeft: '15px' }}>
                A) You suspect a medication is causing side effects and/or wish to ask for dose adjustments. This needs to be discussed with your prescriber or with the Nurse.<br />
                B) You wish to discuss changing to a different medication. This can only be reviewed and approved by your prescriber.<br />
                C) <strong>If you do not have a follow-up appointment already scheduled with your provider</strong>, as we can only provide enough medication until your next appointment.
              </p>
              <strong>If any of these situations apply to you, please contact your provider's office to schedule a follow-up appointment.</strong>

              <div className="row text-center mt-3 g-2">
                <div className="col-6 col-md-3">
                  <a className="button blue w-100" href="tel:+14805420202">Gilbert<br /><span>(480) 542-0202</span></a>
                </div>
                <div className="col-6 col-md-3">
                  <a className="button blue w-100" href="tel:+14805420222">Chandler<br /><span>(480) 542-0222</span></a>
                </div>
                <div className="col-6 col-md-3">
                  <a className="button blue w-100" href="tel:+14805421212">Mesa<br /><span>(480) 542-1212</span></a>
                </div>
                <div className="col-6 col-md-3">
                  <a className="button blue w-100" href="tel:+16025603505">Phoenix<br /><span>(602) 560-3505</span></a>
                </div>
              </div>
              <br />
              When this form is completed appropriately, you should receive a call from your pharmacy within 2–5 days to update
              you on the status of refilling your medication. You will only receive a call back from our staff if we have a
              question regarding your submission.
            </div>

            <form onSubmit={handleSubmit}>
              {/* Appointment Status */}
              <h3 className="mt-5">Appointment Status:</h3>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">Next Appointment (no refills will be sent until scheduled) *</label>
                  <select 
                    name="nextAppointmentStatus" 
                    value={formData.nextAppointmentStatus} 
                    onChange={handleInput} 
                    className="form-select"
                    required
                  >
                    <option value="">Select Status</option>
                    <option value="Scheduled">Scheduled</option>
                    <option value="Need to Schedule">Need to Schedule</option>
                  </select>
                </div>
                
                {formData.nextAppointmentStatus === 'Scheduled' && (
                  <div className="col-6 col-md-3">
                    <label className="form-label">Next Appointment Date *</label>
                    <input 
                      type="date" 
                      name="nextAppointmentDate" 
                      value={formData.nextAppointmentDate || ''} 
                      onChange={handleInput} 
                      className="form-control"
                      required
                      min={new Date().toISOString().split('T')[0]} // Prevents past dates
                    />
                  </div>
                )}
              </div>

              {/* Personal Information */}
              <h3 className="mt-5">Personal Information:</h3>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Patient First Name *</label>
                  <input 
                    type="text" 
                    name="firstName" 
                    value={formData.firstName} 
                    onChange={handleInput} 
                    placeholder="First Name" 
                    className="form-control"
                    required 
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label">Patient Last Name *</label>
                  <input 
                    type="text" 
                    name="lastName" 
                    value={formData.lastName} 
                    onChange={handleInput} 
                    placeholder="Last Name" 
                    className="form-control"
                    required 
                  />
                </div>
              </div> <br />
              <div className="row g-3">
                <div className="col-6 col-md-3">
                  <label className="form-label">Date of Birth *</label>
                  <input 
                    type="date" 
                    name="dob" 
                    value={formData.dob} 
                    onChange={handleInput} 
                    className="form-control"
                    required 
                  />
                </div>
              </div>

              {/* New Allergy Information */}
              <h3 className="mt-5">New Allergy Information:</h3>
              <div className="row">
                <div className="col-12">
                  <label className="form-label">Do you have any new allergies? *</label>
                  <div className="mt-2">
                    <div className="form-check form-check-inline">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="newAllergy" 
                        value="No" 
                        checked={formData.newAllergy === 'No'} 
                        onChange={handleInput} 
                        id="allergyNo"
                      />
                      <label className="form-check-label" htmlFor="allergyNo">No</label>
                    </div>
                    <div className="form-check form-check-inline">
                      <input 
                        className="form-check-input" 
                        type="radio" 
                        name="newAllergy" 
                        value="Yes" 
                        checked={formData.newAllergy === 'Yes'} 
                        onChange={handleInput} 
                        id="allergyYes"
                      />
                      <label className="form-check-label" htmlFor="allergyYes">Yes</label>
                    </div>
                  </div>
                  {formData.newAllergy === 'Yes' && (
                    <div className="mt-3">
                      <input 
                        type="text" 
                        name="newAllergyName" 
                        value={formData.newAllergyName} 
                        onChange={handleInput} 
                        placeholder="Name of new medication allergy *" 
                        className="form-control"
                        required
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Request Information */}
              <h3 className="mt-5">Request Information:</h3>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <label className="form-label">Request Type *</label>
                  <select 
                    name="requestType" 
                    value={formData.requestType} 
                    onChange={handleInput} 
                    className="form-select"
                    required
                  >
                    <option value="">Select Request Type</option>
                    <option value="Refill">Refill</option>
                    <option value="Transfer">Transfer</option>
                    <option value="Early Fill">Early Fill</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Reason or any info needed by nurse to complete the request:</label>
                  <textarea
                    name="reasonDetails"
                    value={formData.reasonDetails}
                    onChange={handleInput}
                    placeholder="Example: SUPPLY ISSUE, TRAVELING OUT OF STATE, ETC"
                    rows="3"
                    className="form-control"
                  />
                </div>
              </div>

              {/* Medication Information */}
              <h3 className="mt-5">Medication Information:</h3>
              <div className="row">
                <div className="col-12">
                  <label className="form-label">Medication Selection *</label>
                  <div className="mt-2">
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="medicationSelection" 
                        value="All Meds" 
                        checked={formData.medicationSelection === 'All Meds'} 
                        onChange={handleInput} 
                        id="allMeds"
                      />
                      <label className="form-check-label" htmlFor="allMeds">All Meds</label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="medicationSelection" 
                        value="Specific medications" 
                        checked={formData.medicationSelection === 'Specific medications'} 
                        onChange={handleInput} 
                        id="specificMeds"
                      />
                      <label className="form-check-label" htmlFor="specificMeds">Specific medications</label>
                    </div>
                  </div>
                </div>
              </div>

              {formData.medicationSelection === 'Specific medications' && (
                <>
                  {medications.map((med, index) => (
                    <div className="border p-3 mb-3 rounded" key={med.id}>
                      <div className="row g-3">
                        <div className="col-12 col-md-8">
                          <label className="form-label">Medication Name</label>
                          <CreatableSelect
                            options={medicationOptions}
                            isClearable
                            placeholder="Start typing or enter a new medication..."
                            inputValue={inputValues[med.id] || ''}
                            onInputChange={(value, { action }) => {
                              if (action !== 'input-blur' && action !== 'menu-close') {
                                setInputValues((prev) => ({ ...prev, [med.id]: value }));
                              }
                            }}
                            onChange={(selected) => {
                              const newValue = selected ? selected.value : '';
                              handleMedicationChange(index, 'name', newValue);
                              setInputValues((prev) => ({ ...prev, [med.id]: '' }));
                            }}
                            menuIsOpen={!!inputValues[med.id]}
                            openMenuOnFocus={false}
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Strength (e.g., 300mg)</label>
                          <input 
                            type="text" 
                            value={med.strength} 
                            onChange={(e) => handleMedicationChange(index, 'strength', e.target.value)} 
                            placeholder="e.g. 300mg" 
                            className="form-control"
                          />
                        </div>
                        {index > 0 && (
                          <div className="col-12">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => removeMedication(med.id)}>
                              Remove Medication
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn btn-primary mb-4" onClick={addMedication}>
                    + Add Another Medication
                  </button>
                </>
              )}

              {/* Pharmacy Section */}
              <h3 className="mt-5">Pharmacy:</h3>
              <div className="row">
                <div className="col-12">
                  <label className="form-label">Pharmacy *</label>
                  <div className="mt-2">
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="pharmacy" 
                        value="Pharmacy on file" 
                        checked={formData.pharmacy === 'Pharmacy on file'} 
                        onChange={handleInput} 
                        id="pharmacyOnFile"
                      />
                      <label className="form-check-label" htmlFor="pharmacyOnFile">Pharmacy on file</label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="pharmacy" 
                        value="Different pharmacy" 
                        checked={formData.pharmacy === 'Different pharmacy'} 
                        onChange={handleInput} 
                        id="differentPharmacy"
                      />
                      <label className="form-check-label" htmlFor="differentPharmacy">Different pharmacy</label>
                    </div>
                  </div>
                </div>
              </div>

              {formData.pharmacy === 'Different pharmacy' && (
                <>
                  <div className="row g-3 mt-3">
                    <div className="col-12">
                      <label className="form-label">Change Type *</label>
                      <div className="mt-2">
                        <div className="form-check form-check-inline">
                          <input 
                            className="form-check-input"
                            type="radio" 
                            name="pharmacyChangeType" 
                            value="One-Time" 
                            checked={formData.pharmacyChangeType === 'One-Time'} 
                            onChange={handleInput} 
                            id="oneTime"
                            required
                          />
                          <label className="form-check-label" htmlFor="oneTime">One-Time</label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input 
                            className="form-check-input"
                            type="radio" 
                            name="pharmacyChangeType" 
                            value="Permanent Change" 
                            checked={formData.pharmacyChangeType === 'Permanent Change'} 
                            onChange={handleInput} 
                            id="permanentChange"
                            required
                          />
                          <label className="form-check-label" htmlFor="permanentChange">Permanent Change</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mt-3">
                    <div className="col-12">
                      <label className="form-label">Pharmacy Type *</label>
                      <div className="mt-2">
                        <div className="form-check form-check-inline">
                          <input 
                            className="form-check-input"
                            type="radio" 
                            name="pharmacyType" 
                            value="local" 
                            checked={formData.pharmacyType === 'local'} 
                            onChange={handleInput} 
                            id="localPharmacyType"
                            required
                          />
                          <label className="form-check-label" htmlFor="localPharmacyType">Local Pharmacy</label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input 
                            className="form-check-input"
                            type="radio" 
                            name="pharmacyType" 
                            value="mail" 
                            checked={formData.pharmacyType === 'mail'} 
                            onChange={handleInput} 
                            id="mailPharmacyType"
                            required
                          />
                          <label className="form-check-label" htmlFor="mailPharmacyType">Mail Order Pharmacy</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {formData.pharmacyType === 'local' && (
                    <>
                      <div className="row g-3 mt-3">
                        <div className="col-12 col-md-6">
                          <label className="form-label">Select Local Pharmacy *</label>
                          <select 
                            name="localPharmacy" 
                            value={formData.localPharmacy} 
                            onChange={handleInput} 
                            className="form-select"
                            required
                          >
                            <option value="">Select</option>
                            <option value="CVS">CVS</option>
                            <option value="Walgreens">Walgreens</option>
                            <option value="Fry's">Fry's</option>
                            <option value="Costco">Costco</option>
                            <option value="Sam's Club">Sam's Club</option>
                            <option value="Target">Target</option>
                            <option value="Walmart">Walmart</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        {formData.localPharmacy === 'Other' && (
                          <div className="col-12 col-md-6">
                            <label className="form-label">Other Local Pharmacy Name *</label>
                            <input 
                              type="text" 
                              name="otherLocalPharmacy" 
                              value={formData.otherLocalPharmacy} 
                              onChange={handleInput} 
                              className="form-control"
                              required
                            />
                          </div>
                        )}
                      </div>
                      <div className="row g-3 mt-3">
                        <div className="col-12">
                          <label className="form-label">Crossroads of pharmacy location *</label>
                          <input 
                            type="text" 
                            name="pharmacyCrossRoads" 
                            value={formData.pharmacyCrossRoads} 
                            onChange={handleInput} 
                            className="form-control"
                            required
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {formData.pharmacyType === 'mail' && (
                    <>
                      <div className="row g-3 mt-3">
                        <div className="col-12 col-md-6">
                          <label className="form-label">Select Mail Order Pharmacy *</label>
                          <select 
                            name="mailOrderPharmacy" 
                            value={formData.mailOrderPharmacy} 
                            onChange={handleInput} 
                            className="form-select"
                            required
                          >
                            <option value="">Select</option>
                            <option value="Express Scripts">Express Scripts</option>
                            <option value="OptumRx">OptumRx</option>
                            <option value="CVS/Caremark">CVS/Caremark</option>
                            <option value="Cigna">Cigna</option>
                            <option value="Walmart">Walmart</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                        {formData.mailOrderPharmacy === 'Other' && (
                          <div className="col-12 col-md-6">
                            <label className="form-label">Other Mail Order Pharmacy Name *</label>
                            <input 
                              type="text" 
                              name="otherMailOrderPharmacy" 
                              value={formData.otherMailOrderPharmacy} 
                              onChange={handleInput} 
                              className="form-control"
                              required
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Contact Information */}
              <h3 className="mt-5">Preferred Contact Method:</h3>
              <div className="row">
                <div className="col-12">
                  <label className="form-label">Preferred Contact *</label>
                  <div className="mt-2">
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="preferredContact" 
                        value="Email" 
                        checked={formData.preferredContact === 'Email'} 
                        onChange={handleInput} 
                        id="contactEmail"
                      />
                      <label className="form-check-label" htmlFor="contactEmail">Email</label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="preferredContact" 
                        value="Phone" 
                        checked={formData.preferredContact === 'Phone'} 
                        onChange={handleInput} 
                        id="contactPhone"
                      />
                      <label className="form-check-label" htmlFor="contactPhone">Phone</label>
                    </div>
                    <div className="form-check">
                      <input 
                        className="form-check-input"
                        type="radio" 
                        name="preferredContact" 
                        value="SMS/Text" 
                        checked={formData.preferredContact === 'SMS/Text'} 
                        onChange={handleInput} 
                        id="contactSMS"
                      />
                      <label className="form-check-label" htmlFor="contactSMS">SMS/Text</label>
                    </div>
                  </div>
                </div>
              </div>

              {formData.preferredContact === 'Email' && (
                <div className="row g-3 mt-3">
                  <div className="col-12">
                    <label className="form-label">Email *</label>
                    <input 
                      type="email" 
                      name="email" 
                      value={formData.email} 
                      onChange={handleInput} 
                      placeholder="you@example.com" 
                      className="form-control"
                      required
                    />
                  </div>
                </div>
              )}

              {(formData.preferredContact === 'Phone' || formData.preferredContact === 'SMS/Text') && (
                <>
                  <div className="row g-3 mt-3">
                    <div className="col-12">
                      <label className="form-label">Phone Number *</label>
                      <PatternFormat
                        name="phone"
                        format="(###) ###-####"
                        mask="_"
                        placeholder="(555) 555-5555"
                        value={formData.phone}
                        onValueChange={(values) => setFormData((prev) => ({ ...prev, phone: values.value }))}
                        className="form-control"
                        required
                      />
                    </div>
                  </div>

                  {formData.preferredContact === 'Phone' && (
                    <div className="row mt-3">
                      <div className="col-12">
                        <label className="form-label">Voicemail Permission</label>
                        <div className="mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="voicemailPermission"
                              value="Yes, you can leave a detailed voice message"
                              checked={formData.voicemailPermission === 'Yes, you can leave a detailed voice message'}
                              onChange={handleInput}
                              id="voicemailYes"
                            />
                            <label className="form-check-label" htmlFor="voicemailYes">
                              Yes, you can leave a detailed voice message
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="voicemailPermission"
                              value="No, please only say you have a message for me and leave Redemption contact name and number"
                              checked={formData.voicemailPermission === 'No, please only say you have a message for me and leave Redemption contact name and number'}
                              onChange={handleInput}
                              id="voicemailNo"
                            />
                            <label className="form-check-label" htmlFor="voicemailNo">
                              No, please only say you have a message for me and leave Redemption contact name and number
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.preferredContact === 'SMS/Text' && (
                    <div className="row mt-3">
                      <div className="col-12">
                        <label className="form-label">SMS Permission</label>
                        <div className="mt-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="smsPermission"
                              value="Yes, you can send me details through SMS/Text Message"
                              checked={formData.smsPermission === 'Yes, you can send me details through SMS/Text Message'}
                              onChange={handleInput}
                              id="smsYes"
                            />
                            <label className="form-check-label" htmlFor="smsYes">
                              Yes, you can send me details through SMS/Text Message
                            </label>
                          </div>
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="smsPermission"
                              value="No, please only text me that you have a message for me and leave Redemption contact name and number"
                              checked={formData.smsPermission === 'No, please only text me that you have a message for me and leave Redemption contact name and number'}
                              onChange={handleInput}
                              id="smsNo"
                            />
                            <label className="form-check-label" htmlFor="smsNo">
                              No, please only text me that you have a message for me and leave Redemption contact name and number
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Submit Button */}
              <div className="row mt-5">
                <div className="col-12 text-center">
                  <button 
                    type="submit" 
                    className="button blue px-5"
                    disabled={isSubmitting || formData.nextAppointmentStatus === 'Need to Schedule'}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Request"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
